import { clsx } from 'clsx';
import type { MouseEventHandler, ReactNode } from 'react';

import type { ReeferBaseProps } from '../../../utils/baseProps.types';
import { Typography } from '../../typography/typography';
import styles from './buttonToggleButton.module.css';

export type SupportedValue = string | number;

export interface ButtonToggleButtonProps<
  T extends SupportedValue = SupportedValue,
> extends Omit<ReeferBaseProps, 'id'> {
  /** Aria label */
  ariaLabel?: string;

  /** Automatically focus this button when rendered */
  autoFocus?: boolean;

  /** Child elements of the `ButtonToggle.Button` */
  children?: ReactNode;

  /** Disable this button option */
  disabled?: boolean;

  /** Stretches the container to fit its parent; buttons positioned with space-between */
  full?: boolean;

  /** Button label */
  label?: string;

  /** `onClick` handler (controlled by parent) */
  onClick?: MouseEventHandler<HTMLButtonElement>;

  /** Whether button is currently selected (controlled by parent) */
  selected?: boolean;

  /** Value that is returned to the `onChange` handler when clicked */
  value: T;

  /** Defines button variant (controlled by parent) */
  variant?: 'primary' | 'inverse' | 'tertiary';
}

/**
 * Use `ButtonToggle.Button` within `ButtonToggle` to define available `ButtonToggle` options.
 */
export function ButtonToggleButton<T extends SupportedValue = SupportedValue>({
  ariaLabel,
  autoFocus,
  children,
  className,
  disabled = false,
  full = true,
  label,
  onClick,
  selected = false,
  'data-testid': testId,
  value,
  variant = 'primary',
}: ButtonToggleButtonProps<T>) {
  return (
    <button
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={clsx(
        className,
        styles['buttonToggleButton'],
        styles[`buttonToggleButton__${variant}`],
        {
          [styles['buttonToggleButton__full']]: full,
        }
      )}
      data-testid={testId}
      data-selected={selected}
      disabled={disabled}
      id={`toggle-${value}`}
      onClick={onClick}
      type="button"
      value={value}
    >
      {!children && (
        <Typography as="span" color="inherit" variant="body-bold">
          {label}
        </Typography>
      )}
      {children && children}
    </button>
  );
}
